import { z } from "zod/v4";

// Club-wide member availability: a recurring weekly pattern plus dated
// exceptions. Distinct from court-availability (a venue's bookable slots) and
// from training availability (the same idea scoped to one season).
//
// Wire contract, agreed with the Go service and the mobile app:
//   - weekday is 0 = Monday .. 6 = Sunday, not JavaScript's getDay()
//   - rule times are "HH:MM" local wall clock, carrying their own timezone
//   - exception bounds are RFC3339 instants
//   - every window is half-open: a rule ending at 18:00 does not cover 18:00

const MONDAY = 0;
const SUNDAY = 6;
const CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const weekdaySchema = z.number().int().min(MONDAY).max(SUNDAY);

export const clockSchema = z.string().regex(CLOCK_PATTERN, "expected HH:MM");

// An enum rather than a boolean so a third answer can arrive without a
// migration on either side of the wire.
export const availabilityResultSchema = z.enum(["available", "unavailable"]);

export const availabilityRuleSchema = z.object({
	club_id: z.string().uuid(),
	end_time: clockSchema,
	id: z.string().uuid(),
	result: availabilityResultSchema,
	start_time: clockSchema,
	timezone: z.string(),
	user_id: z.string().uuid(),
	weekday: weekdaySchema,
});

export const availabilityExceptionSchema = z.object({
	club_id: z.string().uuid(),
	ends_at: z.coerce.date(),
	id: z.string().uuid(),
	note: z.string().nullable().optional(),
	result: availabilityResultSchema,
	starts_at: z.coerce.date(),
	user_id: z.string().uuid(),
});

export const availabilityPatternSchema = z.object({
	exceptions: z.array(availabilityExceptionSchema),
	rules: z.array(availabilityRuleSchema),
});

// A PUT replaces the whole pattern in one transaction, so the request carries
// no ids: per-row edits would surface the database's overlap constraint as a
// conflict on every reorder.
export const setAvailabilityPatternSchema = z.object({
	exceptions: z.array(
		z
			.object({
				ends_at: z.date(),
				note: z.string().nullable().optional(),
				result: availabilityResultSchema.optional(),
				starts_at: z.date(),
			})
			.refine((exception) => exception.ends_at > exception.starts_at, {
				message: "End must be after start.",
				path: ["ends_at"],
			}),
	),
	rules: z.array(
		z
			.object({
				end_time: clockSchema,
				result: availabilityResultSchema.optional(),
				start_time: clockSchema,
				timezone: z.string().optional(),
				weekday: weekdaySchema,
			})
			.refine((rule) => rule.end_time > rule.start_time, {
				message: "End must be after start.",
				path: ["end_time"],
			}),
	),
});

// One cell of the weekly grid. Only cells with at least one available member
// are emitted, so an empty slot list means nobody has submitted a pattern —
// not that nobody is free. The two must render differently.
export const availabilitySlotSchema = z.object({
	available_count: z.number().int(),
	end_time: clockSchema,
	start_time: clockSchema,
	weekday: weekdaySchema,
});

export const availabilityDateCountSchema = z.object({
	available_count: z.number().int(),
	date: z.string(),
});

export const availabilityHeatmapSchema = z.object({
	date_overrides: z.array(availabilityDateCountSchema),
	slots: z.array(availabilitySlotSchema),
	total_members: z.number().int(),
});

export type AvailabilityResult = z.infer<typeof availabilityResultSchema>;
export type AvailabilityRule = z.infer<typeof availabilityRuleSchema>;
export type AvailabilityException = z.infer<typeof availabilityExceptionSchema>;
export type AvailabilityPattern = z.infer<typeof availabilityPatternSchema>;
export type SetAvailabilityPattern = z.infer<typeof setAvailabilityPatternSchema>;
export type AvailabilitySlot = z.infer<typeof availabilitySlotSchema>;
export type AvailabilityDateCount = z.infer<typeof availabilityDateCountSchema>;
export type AvailabilityHeatmap = z.infer<typeof availabilityHeatmapSchema>;
